import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { AudioPlayerProvider } from './contexts/AudioPlayerContext';
import { UploadProvider } from './contexts/UploadContext';
import { UserFilesProvider } from './contexts/UserFilesContext';
import { CollectionsProvider } from './contexts/CollectionsContext';
import FloatingPlayer from './components/FloatingPlayer';
import { EmailConsentBanner } from './components/EmailConsentBanner';
import {
  subscribeEmailConsentSuppression,
  getEmailConsentSuppressed,
} from './components/emailConsentSuppress';
import './App.css';
import Home from './pages/Home';
import LoadingOverlay from './components/LoadingOverlay';
// Eager — trivial synchronous redirect, must not flash a Suspense fallback.
import ProfileIndexRedirect from './pages/Profile/ProfileIndexRedirect';

// Lazy load all non-home pages for better initial load performance
const Player = lazy(() => import('./pages/Player'));
const StatsPage = lazy(() => import('./pages/StatsPage'));
const UserFiles = lazy(() => import('./pages/UserFiles'));
const UploadPage = lazy(() => import('./pages/UploadPage'));
const RecordPage = lazy(() => import('./pages/RecordPage'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const DMCAPage = lazy(() => import('./pages/DMCAPage'));
const LogoutSuccess = lazy(() => import('./pages/LogoutSuccess'));
const AdminStatus = lazy(() => import('./pages/AdminStatus'));
const Pro = lazy(() => import('./pages/Pro'));
const ProfileLayout = lazy(() => import('./pages/Profile/ProfileLayout'));
const ProfileAccountTab = lazy(() => import('./pages/Profile/ProfileAccountTab'));
const ProfilePlanTab = lazy(() => import('./pages/Profile/ProfilePlanTab'));
const ProfileNotificationsTab = lazy(() => import('./pages/Profile/ProfileNotificationsTab'));
const ProfileExperimentsTab = lazy(() => import('./pages/Profile/ProfileExperimentsTab'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const HowToUpload = lazy(() => import('./pages/HowToUpload'));
const HowToShare = lazy(() => import('./pages/HowToShare'));
const HowToConvert = lazy(() => import('./pages/HowToConvert'));
const AudioEditor = lazy(() => import('./components/AudioEditor'));
const Collections = lazy(() => import('./pages/Collections'));
const CollectionLayout = lazy(() => import('./components/CollectionLayout'));
const CollectionTracks = lazy(() => import('./pages/CollectionTracks'));
const CollectionSettings = lazy(() => import('./pages/CollectionSettings'));
const ShortLinkPlayer = lazy(() => import('./pages/ShortLinkPlayer'));
const Embed = lazy(() => import('./pages/Embed'));

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Note: the global feedback banner was retired entirely (see the home top
// slot refactor). If a feedback ask returns later, the right home is a new
// BannerSpec in the HomeTopSlot registry, not a re-introduced cross-page
// app-shell mount.

// Same reasoning that drove the feedback-banner suppression on /pro: the
// email-consent modal auto-opens
// 1.5s after first visit, and on /pro that interruption competes with the
// upgrade CTA. Suppress on the conversion page; let it show everywhere else.
//
// Also suppressed on the NotFound page (mounted from pages/Player when a
// share link 404s). NotFound isn't a route — it's a conditional render on
// /player/:fileId — so a pathname check can't reach it. Instead, NotFound
// flips a small external store on mount (components/emailConsentSuppress)
// and the wrapper consults it here. Reasoning is the same as /pro: the 404
// page just got a dropzone + sign-up beat of its own (commit 564ece8) and
// an interruption modal at t=1.5s breaks that micro-conversion the same
// way it breaks /pro's upgrade flow.
const RouteAwareEmailConsentBanner: React.FC = () => {
  const { pathname } = useLocation();
  const suppressed = React.useSyncExternalStore(
    subscribeEmailConsentSuppression,
    getEmailConsentSuppressed,
    getEmailConsentSuppressed,
  );
  if (pathname === '/pro') return null;
  if (suppressed) return null;
  return <EmailConsentBanner />;
};

// Bare /blog → /blog/. The Jekyll blog is served in prod by a CloudFront
// ordered cache behavior on path_pattern "/blog/*" plus a CloudFront Function
// (infra/code/cloudfront_blog_function.tf) that rewrites /blog/ → /blog/index.html.
// The "/blog/*" pattern does NOT match a bare /blog, so that request falls
// into the SPA default behavior and gets swallowed by the /:handle catch-all.
// This component issues a full browser navigation (not a Router <Navigate>) so
// the next request goes back through CloudFront and lands on Jekyll.
//
// IMPORTANT — pathname guard, not just a useEffect.
// React Router 7 matches path="/blog" against BOTH /blog and /blog/ (trailing
// slashes are ignored by default). So if the SPA ever re-mounts at /blog/ for
// any reason — dev server (no Jekyll on localhost), or prod with the Jekyll
// deploy missing, or a cold-cache miss that falls through to the SPA — this
// route re-runs the redirect, the browser reloads to the same URL, the SPA
// re-mounts, the route re-runs… infinite reload loop. ("Blinking like crazy"
// in the address bar, exact symptom Glenn hit during local dev.)
// Fix: only call replace() when pathname is exactly "/blog" (no trailing
// slash). If we somehow render at "/blog/", we're already past the redirect
// and CloudFront/Jekyll didn't take over — render a quiet fallback link
// instead of reloading.
const BlogRedirect: React.FC = () => {
  const [needsFallback, setNeedsFallback] = React.useState(false);
  React.useEffect(() => {
    if (window.location.pathname === '/blog') {
      window.location.replace('/blog/');
    } else {
      // We're at /blog/ (or some other trailing-slash variant) and still
      // mounted in the SPA — Jekyll isn't serving this path right now.
      // Don't reload; show a calm fallback.
      setNeedsFallback(true);
    }
  }, []);
  if (!needsFallback) return null;
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--background-primary)] text-white px-4">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold mb-3">Blog</h1>
        <p className="text-white/60 mb-6">The blog isn’t available right now.</p>
        <a
          href="/"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-soundwave-500 hover:bg-soundwave-400 text-white font-semibold text-sm transition-colors"
        >
          Back to PasteWaves
        </a>
      </div>
    </div>
  );
};

// AppContent component that can access auth context
const AppContent: React.FC = () => {
  const { authStatus } = useAuth();

  React.useEffect(() => {
    console.log('[App] authStatus:', authStatus);
  }, [authStatus]);

  return (
    <>
      <AnimatePresence>
        {authStatus === 'authenticating' && <LoadingOverlay />}
      </AnimatePresence>
      <AudioPlayerProvider>
        <UserFilesProvider>
          <CollectionsProvider>
            <UploadProvider>
              <Router>
            <ScrollToTop />
            <RouteAwareEmailConsentBanner />
            <Suspense fallback={<LoadingOverlay />}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/record" element={<RecordPage />} />
                <Route path="/player/:fileId" element={<Player />} />
                <Route path="/embed/:fileId" element={<Embed />} />
                <Route path="/stats/:fileId" element={<StatsPage />} />
                <Route path="/editor/:fileId" element={<AudioEditor />} />
                <Route path="/c/:shortId" element={<ShortLinkPlayer />} />
                <Route path="/collections" element={<Collections />} />
                <Route path="/collection/:collectionId" element={<CollectionLayout />}>
                  <Route index element={<CollectionTracks />} />
                  <Route path="settings" element={<CollectionSettings />} />
                </Route>
                <Route path="/my-files" element={<UserFiles />} />
                <Route path="/profile" element={<ProfileLayout />}>
                  <Route index element={<ProfileIndexRedirect />} />
                  <Route path="account" element={<ProfileAccountTab />} />
                  <Route path="plan" element={<ProfilePlanTab />} />
                  <Route path="notifications" element={<ProfileNotificationsTab />} />
                  <Route path="experiments" element={<ProfileExperimentsTab />} />
                </Route>
                {/* /blog is a separate Jekyll site under /blog/* served directly
                    by CloudFront's ordered cache behavior (infra/code/main.tf
                    line ~155 + cloudfront_blog_function.tf). CloudFront's
                    path_pattern "/blog/*" matches /blog/anything but does NOT
                    match a bare /blog (no trailing slash, nothing after) — so
                    that request falls through to the SPA shell and, with no
                    matching React Router route, gets eaten by the /:handle
                    catch-all below (rendering the "no profile" 404 state of
                    PublicProfilePage). Fix: a tiny redirect-to-/blog/ route
                    that forces a full browser navigation, which then matches
                    the CloudFront /blog/* rule and renders the actual blog. */}
                <Route path="/blog" element={<BlogRedirect />} />
                {/* Vanity profile: /@handle. RR7 won't match a literal '@' prefix
                    inside a param segment, so we match the whole segment and guard
                    on the '@' in the component. React Router matches by
                    specificity (static segments beat dynamic), so static routes
                    above always win — but anything new that's a bare single
                    segment (like /blog) needs its own route to avoid being
                    swallowed here. */}
                <Route path="/:handle" element={<PublicProfilePage />} />
                <Route path="/admin/status" element={<AdminStatus />} />
                <Route path="/pro" element={<Pro />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/dmca" element={<DMCAPage />} />
                <Route path="/copyright" element={<DMCAPage />} />
                <Route path="/logout-success" element={<LogoutSuccess />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/guide/upload-audio" element={<HowToUpload />} />
                <Route path="/guide/share-audio" element={<HowToShare />} />
                <Route path="/guide/convert-mp3" element={<HowToConvert />} />
              </Routes>
            </Suspense>
            <FloatingPlayer />
              </Router>
            </UploadProvider>
          </CollectionsProvider>
        </UserFilesProvider>
      </AudioPlayerProvider>
    </>
  );
};

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <ToastProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;